import { useState } from 'react';
import { Lock, ArrowUpCircle } from 'lucide-react';
import { PLAN_HIERARCHY, getMinPlanForFeature } from '../../utils/featureConfig';
import { useCopy } from '../../i18n';
import PricingPage from './PricingPage';
import analytics from '../../services/analyticsService';
import { Button } from '../ui/button';

interface UpgradeOverlayProps {
  feature: string;
  children: React.ReactNode;
}

export default function UpgradeOverlay({ feature, children }: UpgradeOverlayProps) {
  const [showPricing, setShowPricing] = useState(false);

  const copy = useCopy().account;
  const t = copy.billing.upgrade;
  // O `feature` é a chave do banco: ela indexa a cópia, nunca vira texto.
  const config = copy.features[feature as keyof typeof copy.features];
  const minPlan = getMinPlanForFeature(feature);
  // Nome comercial do plano, igual em qualquer idioma.
  const planDisplay = PLAN_HIERARCHY[minPlan]?.displayName || 'Starter';

  return (
    <>
      <div className="relative w-full h-full min-h-[300px] overflow-hidden">
        {children && (
          <div className="blur-[3px] pointer-events-none select-none opacity-50 w-full h-full">
            {children}
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/15">
          <div className="flex flex-col items-center text-center py-10 px-12 bg-[var(--color-bg)] rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)] max-w-[400px] w-[90%]">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] mb-5">
              <Lock size={32} />
            </div>
            <h3 className="text-xl font-semibold text-[var(--color-text)] m-0 mb-2">
              {config?.name || t.defaultTitle}
            </h3>
            <p className="text-sm text-[var(--color-text-secondary)] m-0 mb-5 leading-relaxed">
              {config?.description || t.defaultBody}
            </p>
            <div className="inline-flex items-center gap-1 py-1.5 px-4 bg-[var(--color-info-bg)] text-[var(--color-info-text)] rounded-[20px] text-[13px] mb-5">
              {t.availablePre}<strong className="font-semibold">{planDisplay}</strong>{t.availablePost}
            </div>
            <Button
              variant="primary"
              size="lg"
              className="bg-[var(--color-info)] hover:bg-[var(--color-info-dark)]"
              onClick={() => { analytics.trackPlanUpgradeClick('feature_gate'); setShowPricing(true); }}
            >
              <ArrowUpCircle size={18} />
              {t.upgrade}
            </Button>
          </div>
        </div>
      </div>
      {showPricing && <PricingPage onClose={() => setShowPricing(false)} />}
    </>
  );
}
